import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSave, 
  faUndo, 
  faChartLine, 
  faFilter,
  faCheckCircle,
  faTimesCircle,
  faSpinner,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import {
  getDisciplinasDisponiveis,
  getCursosPorDisciplina,
  getAlunosPorDisciplina,
  getEstatisticasDisciplina,
  atualizarNotasLote,
  getExtensoesUsuario,
  removerNota
} from '../../services/lancamentoNotasService';
import { useAuth } from '../../contexts/AuthContext';
import '../../styles/LancamentoNotas.css';

function LancamentoNotas() {
  // Auth Context
  const { user: authUser, hasPermission } = useAuth();
  
  // Estados principais
  const [disciplinas, setDisciplinas] = useState([]);
  const [cursos, setCursos] = useState([]);
  const [extensoes, setExtensoes] = useState([]);
  const [alunos, setAlunos] = useState([]);
  const [estatisticas, setEstatisticas] = useState(null);
  
  // Estados de filtro
  const [selectedDisciplina, setSelectedDisciplina] = useState('');
  const [selectedCurso, setSelectedCurso] = useState('');
  const [selectedExtensao, setSelectedExtensao] = useState('');
  
  // Estados de UI
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [showFilters, setShowFilters] = useState(true);
  const [notasEditadas, setNotasEditadas] = useState({});
  const [notasInvalidas, setNotasInvalidas] = useState({}); // Rastreia notas com valores inválidos
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [podeRemoverNotas, setPodeRemoverNotas] = useState(false);
  
  // Estados de paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalAlunos, setTotalAlunos] = useState(0);
  
  // Ref para controlar se é a primeira renderização do debounce
  const isFirstSearchRender = useRef(true);

  // Funções auxiliares
  const mostrarMensagem = useCallback((texto, tipo = 'info') => {
    setMessage({ texto, tipo });
    setTimeout(() => setMessage(null), 5000);
  }, []);

  // Funções de carregamento
  const carregarDisciplinas = useCallback(async () => {
    try {
      setLoading(true);
      const filters = {};
      if (selectedExtensao) filters.extensao = selectedExtensao;
      
      const data = await getDisciplinasDisponiveis(filters);
      setDisciplinas(data);
    } catch (error) {
      mostrarMensagem('Erro ao carregar disciplinas', 'error');
    } finally {
      setLoading(false);
    }
  }, [selectedExtensao, mostrarMensagem]);

  const carregarExtensoesUsuario = useCallback(async () => {
    try {
      setLoading(true);
      const extensoesData = await getExtensoesUsuario();
      console.log('Extensões carregadas:', extensoesData);
      setExtensoes(extensoesData);
    } catch (error) {
      console.error('Erro ao carregar extensões:', error);
      mostrarMensagem('Erro ao carregar extensões do usuário', 'error');
    } finally {
      setLoading(false);
    }
  }, [mostrarMensagem]);

  const carregarCursos = useCallback(async () => {
    try {
      const cursosData = await getCursosPorDisciplina(selectedDisciplina);
      setCursos(cursosData);
    } catch (error) {
      console.error('Erro ao carregar cursos:', error);
      mostrarMensagem('Erro ao carregar cursos', 'error');
    }
  }, [selectedDisciplina, mostrarMensagem]);

  const carregarAlunos = useCallback(async (pagina = 1) => {
    // Carrega alunos se pelo menos disciplina estiver selecionada
    if (!selectedDisciplina) {
      setAlunos([]);
      setEstatisticas(null);
      setTotalAlunos(0);
      return;
    }

    // Se tiver muitas extensões disponíveis (mais de 10) e nenhuma foi selecionada,
    // exigir que o usuário selecione uma extensão para evitar timeout
    if (extensoes.length > 10 && !selectedExtensao) {
      mostrarMensagem('Por favor, selecione uma extensão para filtrar os alunos', 'warning');
      setAlunos([]);
      setEstatisticas(null);
      setTotalAlunos(0);
      return;
    }

    try {
      setLoading(true);
      const filters = {};
      if (selectedCurso) filters.curso = selectedCurso;
      if (selectedExtensao) filters.extensao = selectedExtensao;

      console.log('⚡ Carregando página', pagina, 'com filtros:', {
        disciplina: selectedDisciplina,
        filters,
        itemsPerPage
      });

      // OTIMIZAÇÃO: Página 1 carrega estatísticas e dados EM PARALELO
      if (pagina === 1) {
        const startTime = Date.now();
        
        const [statsData, alunosResponse] = await Promise.all([
          getEstatisticasDisciplina(selectedDisciplina, filters),
          getAlunosPorDisciplina(selectedDisciplina, { 
            ...filters, 
            page: 1, 
            limit: itemsPerPage,
            search: debouncedSearchTerm,
            skipCount: false // Primeira página calcula count
          })
        ]);
        
        const duration = Date.now() - startTime;
        console.log(`⚡ Primeira carga completa em ${duration}ms`);
        
        setEstatisticas(statsData);
        setTotalAlunos(statsData.totalAlunos);
        setAlunos(alunosResponse.alunos || []);
        
        if (alunosResponse.pagination?.total) {
          setTotalAlunos(alunosResponse.pagination.total);
        }
      } else {
        // Páginas seguintes: apenas dados, SEM estatísticas, SEM count
        const response = await getAlunosPorDisciplina(selectedDisciplina, { 
          ...filters, 
          page: pagina, 
          limit: itemsPerPage,
          search: debouncedSearchTerm,
          skipCount: true // Pular COUNT em páginas seguintes
        });

        console.log(`✅ Página ${pagina} carregada: ${response.alunos?.length || 0} alunos`);
        
        setAlunos(response.alunos || []);
        
        // Atualizar total apenas se veio da resposta
        if (response.pagination && response.pagination.total) {
          setTotalAlunos(response.pagination.total);
        }
      }

      // Limpar notas editadas apenas se mudou filtros (não se só mudou página)
      if (pagina === 1) {
        setNotasEditadas({});
      }
    } catch (error) {
      console.error('Erro ao carregar alunos:', error);
      mostrarMensagem('Erro ao carregar alunos', 'error');
    } finally {
      setLoading(false);
    }
  }, [selectedDisciplina, selectedCurso, selectedExtensao, extensoes.length, itemsPerPage, debouncedSearchTerm, mostrarMensagem]);

  // Carregar extensões e disciplinas ao montar o componente
  useEffect(() => {
    carregarExtensoesUsuario();
    carregarDisciplinas();
    verificarPermissoes();
  }, [carregarExtensoesUsuario, carregarDisciplinas, authUser]); // eslint-disable-line react-hooks/exhaustive-deps

  // Recarregar disciplinas quando extensão mudar (para atualizar contadores)
  useEffect(() => {
    if (extensoes.length > 0) {
      carregarDisciplinas();
      // Limpar disciplina selecionada ao trocar extensão
      if (selectedExtensao) {
        setSelectedDisciplina('');
        setAlunos([]);
      }
    }
  }, [selectedExtensao]); // eslint-disable-line react-hooks/exhaustive-deps

  // Carregar cursos quando disciplina mudar
  useEffect(() => {
    if (selectedDisciplina) {
      carregarCursos();
    } else {
      setCursos([]);
      setAlunos([]);
      setSelectedCurso('');
    }
  }, [selectedDisciplina, carregarCursos]);

  // Carregar alunos quando disciplina, extensão ou curso mudarem
  useEffect(() => {
    if (selectedDisciplina) {
      // Só carrega se:
      // 1. Não tiver extensões carregadas ainda (ainda está carregando)
      // 2. Tiver 10 ou menos extensões (pode carregar sem selecionar)
      // 3. Tiver uma extensão selecionada
      if (extensoes.length === 0 || extensoes.length <= 10 || selectedExtensao) {
        setCurrentPage(1); // Reset para página 1 ao mudar filtros
        carregarAlunos(1);
      }
    } else {
      setAlunos([]);
      setTotalAlunos(0);
    }
  }, [selectedDisciplina, selectedExtensao, selectedCurso, extensoes.length, itemsPerPage]); // Adicionado itemsPerPage aqui

  // Debounce para o termo de busca (aguarda 500ms após parar de digitar)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Buscar alunos quando o termo de busca (debounced) mudar
  useEffect(() => {
    // Ignorar a primeira renderização (quando o componente monta)
    if (isFirstSearchRender.current) {
      isFirstSearchRender.current = false;
      return;
    }
    
    // Só busca se tiver disciplina selecionada
    if (selectedDisciplina) {
      // E se os filtros de extensão estiverem ok
      if (extensoes.length === 0 || extensoes.length <= 10 || selectedExtensao) {
        console.log('🔍 Buscando por termo:', debouncedSearchTerm);
        setCurrentPage(1);
        carregarAlunos(1);
      }
    }
  }, [debouncedSearchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  // Carregar nova página quando usuário navega (sem resetar currentPage)
  useEffect(() => {
    // Só carregar se já tiver alunos (não é primeira carga) e mudou a página
    const isFirstLoad = currentPage === 1 && alunos.length === 0;
    if (selectedDisciplina && !isFirstLoad) {
      carregarAlunos(currentPage);
    }
  }, [currentPage]); // Apenas currentPage aqui

  // Verificar permissões do usuário
  const verificarPermissoes = useCallback(() => {
    try {
      // Buscar do AuthContext
      if (!authUser) {
        console.log('⚠️ Usuário não autenticado ainda');
        setPodeRemoverNotas(false);
        return;
      }
      
      // Usar a função hasPermission do context
      const podeRemover = hasPermission('academico.notas.delete');
      
      console.log('👤 Permissões do usuário:', {
        usuario: authUser.nome,
        totalPermissoes: authUser.permissoes?.length || 0,
        permissoes: authUser.permissoes?.map(p => p.nome) || [],
        podeRemoverNotas: podeRemover
      });
      
      setPodeRemoverNotas(podeRemover);
    } catch (error) {
      console.error('Erro ao verificar permissões:', error);
    }
  }, [authUser, hasPermission]);

  const handleRemoverNota = async (alunoId, idNota) => {
    if (!podeRemoverNotas) {
      mostrarMensagem('Você não tem permissão para remover notas', 'error');
      return;
    }

    if (!window.confirm('Tem certeza que deseja remover esta nota? Esta ação não pode ser desfeita.')) {
      return;
    }

    try {
      setLoading(true);
      await removerNota(idNota);
      mostrarMensagem('Nota removida com sucesso', 'success');
      
      // Recarregar alunos
      carregarAlunos();
    } catch (error) {
      console.error('Erro ao remover nota:', error);
      if (error.response?.status === 403) {
        mostrarMensagem('Você não tem permissão para remover notas', 'error');
      } else {
        mostrarMensagem('Erro ao remover nota', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleNotaChange = (idPessoa, valor) => {
    // Permitir campo vazio
    if (valor === '') {
      setNotasEditadas(prev => ({
        ...prev,
        [idPessoa]: valor
      }));
      // Remover da lista de inválidas
      setNotasInvalidas(prev => {
        const newState = { ...prev };
        delete newState[idPessoa];
        return newState;
      });
      return;
    }

    const notaNum = parseFloat(valor);
    
    // Verificar se é um número válido e está no range 0-10
    if (isNaN(notaNum) || notaNum < 0 || notaNum > 10) {
      // Marcar como inválida mas ainda permitir digitação para correção
      setNotasEditadas(prev => ({
        ...prev,
        [idPessoa]: valor
      }));
      setNotasInvalidas(prev => ({
        ...prev,
        [idPessoa]: true
      }));
    } else {
      // Nota válida
      setNotasEditadas(prev => ({
        ...prev,
        [idPessoa]: valor
      }));
      // Remover da lista de inválidas
      setNotasInvalidas(prev => {
        const newState = { ...prev };
        delete newState[idPessoa];
        return newState;
      });
    }
  };

  const salvarNotas = async () => {
    const notasParaSalvar = Object.entries(notasEditadas)
      .filter(([_, valor]) => valor !== '' && valor !== null)
      .map(([idPessoa, media]) => {
        // Buscar o aluno correspondente para obter todos os dados necessários
        const aluno = alunos.find(a => a.idPessoa === parseInt(idPessoa));
        
        return {
          id: aluno?.id || null, // null se o aluno não tem registro de nota ainda
          idPessoa: parseInt(idPessoa),
          idProduto: selectedDisciplina,
          curso: aluno?.curso,
          extensao: aluno?.extensao,
          media: media === '' ? null : parseFloat(media),
          dataRealizacao: new Date().toISOString().split('T')[0]
        };
      });

    if (notasParaSalvar.length === 0) {
      mostrarMensagem('Nenhuma nota para salvar', 'warning');
      return;
    }

    // Verificar se há notas inválidas no estado
    if (Object.keys(notasInvalidas).length > 0) {
      mostrarMensagem('Corrija as notas inválidas antes de salvar. As notas devem estar entre 0 e 10.', 'error');
      return;
    }

    // Validar notas (verificação adicional)
    const notasComErro = notasParaSalvar.filter(n => 
      n.media !== null && (isNaN(n.media) || n.media < 0 || n.media > 10)
    );

    if (notasComErro.length > 0) {
      mostrarMensagem('Existem notas inválidas. As notas devem estar entre 0 e 10.', 'error');
      return;
    }

    try {
      setSaving(true);
      const result = await atualizarNotasLote(notasParaSalvar);
      mostrarMensagem(`${result.count} nota(s) salva(s) com sucesso!`, 'success');
      setNotasEditadas({});
      carregarAlunos(); // Recarregar para atualizar estatísticas
    } catch (error) {
      mostrarMensagem('Erro ao salvar notas', 'error');
    } finally {
      setSaving(false);
    }
  };

  const limparNotas = () => {
    if (Object.keys(notasEditadas).length > 0) {
      if (window.confirm('Deseja descartar todas as alterações não salvas?')) {
        setNotasEditadas({});
        setNotasInvalidas({}); // Limpar também as notas inválidas
      }
    }
  };

  const getNotaAtual = (aluno) => {
    // Usar idPessoa como chave porque alunos sem nota têm id = null
    return notasEditadas[aluno.idPessoa] !== undefined 
      ? notasEditadas[aluno.idPessoa] 
      : (aluno.media || '');
  };

  const getNotaStatus = (nota, idPessoa = null) => {
    if (!nota && nota !== 0) return '';
    
    // Verificar se está marcada como inválida
    if (idPessoa && notasInvalidas[idPessoa]) return 'invalida';
    
    const notaNum = parseFloat(nota);
    
    // Verificar se é um número válido e está no range
    if (isNaN(notaNum) || notaNum < 0 || notaNum > 10) return 'invalida';
    
    if (notaNum >= 6) return 'aprovado';
    if (notaNum >= 5) return 'recuperacao';
    return 'reprovado';
  };

  // Filtrar alunos localmente para feedback instantâneo enquanto digita
  // O backend também filtra após o debounce para buscar em todas as páginas
  const alunosFiltrados = alunos.filter(aluno => {
    if (!searchTerm) return true;
    const termo = searchTerm.toLowerCase();
    return (
      aluno.nomeAluno?.toLowerCase().includes(termo) ||
      aluno.rgAluno?.toLowerCase().includes(termo) ||
      aluno.matricula?.toLowerCase().includes(termo)
    );
  });

  // Paginação baseada no backend (não faz slice, backend já paginou)
  const totalPages = Math.ceil(totalAlunos / itemsPerPage);
  const alunosPaginados = alunosFiltrados; // Backend já retorna apenas a página atual

  const temNotasEditadas = Object.keys(notasEditadas).length > 0;

  return (
    <div className="lancamento-notas-container">
      {/* Header */}
      <div className="page-header">
        <h1>Lançamento de Notas</h1>
        <div className="header-actions">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="btn-filter"
            title="Filtros"
          >
            <FontAwesomeIcon icon={faFilter} />
            <span className="btn-text">Filtros</span>
          </button>
        </div>
      </div>

      {/* Mensagem de feedback */}
      {message && (
        <div className={`alert alert-${message.tipo}`}>
          {message.texto}
        </div>
      )}

      {/* Filtros */}
      {showFilters && (
        <div className="filters-section">
          <div className="filters-grid">
            {/* EXTENSÃO PRIMEIRO - Obrigatória se tiver mais de 10 */}
            {extensoes.length > 0 && (
              <div className="form-group">
                <label>Extensão {extensoes.length > 10 ? '*' : '(opcional)'}</label>
                <select
                  value={selectedExtensao}
                  onChange={(e) => setSelectedExtensao(e.target.value)}
                  className="form-control"
                  disabled={loading}
                >
                  <option value="">
                    {extensoes.length > 10 
                      ? 'Selecione uma extensão' 
                      : 'Todas as extensões permitidas'}
                  </option>
                  {extensoes.map(e => (
                    <option key={e.id} value={e.id}>
                      {e.nome}
                    </option>
                  ))}
                </select>
                {extensoes.length > 10 && !selectedExtensao && (
                  <small className="form-text text-warning">
                    ⚠️ Selecione uma extensão antes de escolher a disciplina
                  </small>
                )}
              </div>
            )}

            {/* DISCIPLINA DEPOIS - Só disponível se extensão estiver OK */}
            <div className="form-group">
              <label>Disciplina *</label>
              <select
                value={selectedDisciplina}
                onChange={(e) => {
                  setSelectedDisciplina(e.target.value);
                  setSelectedCurso('');
                  setAlunos([]);
                }}
                className="form-control"
                disabled={loading || (extensoes.length > 10 && !selectedExtensao)}
              >
                <option value="">
                  {extensoes.length > 10 && !selectedExtensao 
                    ? 'Selecione uma extensão primeiro' 
                    : 'Selecione uma disciplina'}
                </option>
                {disciplinas.map(d => (
                  <option key={d.id} value={d.id}>
                    {d.nome}
                  </option>
                ))}
              </select>
            </div>

            {selectedDisciplina && cursos.length > 0 && (
              <div className="form-group">
                <label>Curso (opcional)</label>
                <select
                  value={selectedCurso}
                  onChange={(e) => setSelectedCurso(e.target.value)}
                  className="form-control"
                  disabled={loading}
                >
                  <option value="">Todos os cursos</option>
                  {cursos.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.nome} ({c.totalAlunos} alunos)
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {selectedDisciplina && selectedExtensao && (
            <div className="form-group">
              <label>Buscar aluno</label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Digite o nome ou RG do aluno..."
                className="form-control"
              />
            </div>
          )}
        </div>
      )}

      {/* Mensagem de orientação */}
      {!selectedDisciplina && (
        <div className="hint-text" style={{ textAlign: 'center', padding: '40px 20px', color: '#666' }}>
          <FontAwesomeIcon icon={faFilter} size="3x" style={{ marginBottom: '20px', opacity: 0.3 }} />
          <p>Selecione uma disciplina para começar</p>
        </div>
      )}

      {selectedDisciplina && !selectedExtensao && (
        <div className="hint-text" style={{ textAlign: 'center', padding: '40px 20px', color: '#666' }}>
          <FontAwesomeIcon icon={faFilter} size="3x" style={{ marginBottom: '20px', opacity: 0.3 }} />
          <p>Selecione uma extensão para visualizar os alunos</p>
          <small>A extensão define a turma/polo dos alunos</small>
        </div>
      )}

      {/* Estatísticas */}
      {estatisticas && alunos.length > 0 && (
        <div className="stats-section">
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon total">
                <FontAwesomeIcon icon={faChartLine} />
              </div>
              <div className="stat-content">
                <div className="stat-value">{estatisticas.totalAlunos || 0}</div>
                <div className="stat-label">Total de Alunos</div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon success">
                <FontAwesomeIcon icon={faCheckCircle} />
              </div>
              <div className="stat-content">
                <div className="stat-value">{estatisticas.alunosComNota || 0}</div>
                <div className="stat-label">Com Nota</div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon warning">
                <FontAwesomeIcon icon={faTimesCircle} />
              </div>
              <div className="stat-content">
                <div className="stat-value">{estatisticas.alunosSemNota || 0}</div>
                <div className="stat-label">Sem Nota</div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon info">
                <FontAwesomeIcon icon={faChartLine} />
              </div>
              <div className="stat-content">
                <div className="stat-value">
                  {estatisticas.mediaGeral ? parseFloat(estatisticas.mediaGeral).toFixed(2) : '-'}
                </div>
                <div className="stat-label">Média Geral</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabela de Alunos */}
      {selectedDisciplina && (
        <div className="alunos-section">
          {loading ? (
            <div className="loading-state">
              <FontAwesomeIcon icon={faSpinner} spin size="2x" />
              <p>Carregando alunos...</p>
            </div>
          ) : alunosFiltrados.length === 0 ? (
            <div className="empty-state">
              <p>Nenhum aluno encontrado {searchTerm ? `para "${searchTerm}"` : 'para os filtros selecionados'}</p>
              <small>
                {searchTerm 
                  ? 'Aguarde a busca completa ou tente outro termo' 
                  : 'Verifique se há alunos matriculados nas extensões que você tem acesso'}
              </small>
            </div>
          ) : (
            <>
              {/* Informações e Paginação */}
              <div className="pagination-info" style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                marginBottom: '1rem',
                padding: '0.75rem',
                background: '#f8f9fa',
                borderRadius: '8px',
                flexWrap: 'wrap',
                gap: '1rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: '500', color: '#495057' }}>
                    {searchTerm 
                      ? `${alunosFiltrados.length} aluno(s) na página atual` 
                      : `${totalAlunos} aluno(s) no total`}
                    {searchTerm && searchTerm !== debouncedSearchTerm && ' (buscando...)'}
                  </span>
                  
                  {totalAlunos > 10 && (
                    <>
                      <span style={{ color: '#6c757d' }}>|</span>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0 }}>
                        <span style={{ fontSize: '0.9rem', color: '#495057' }}>Por página:</span>
                        <select
                          value={itemsPerPage}
                          onChange={(e) => {
                            setItemsPerPage(Number(e.target.value));
                            setCurrentPage(1);
                          }}
                          style={{
                            padding: '0.25rem 0.5rem',
                            border: '1px solid #ced4da',
                            borderRadius: '4px',
                            fontSize: '0.9rem',
                            cursor: 'pointer'
                          }}
                        >
                          <option value={10}>10</option>
                          <option value={20}>20</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                        </select>
                      </label>
                    </>
                  )}
                </div>

                {totalPages > 1 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      style={{
                        padding: '0.25rem 0.75rem',
                        border: '1px solid #ced4da',
                        borderRadius: '4px',
                        background: currentPage === 1 ? '#e9ecef' : 'white',
                        cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                        fontSize: '0.9rem'
                      }}
                    >
                      ‹ Anterior
                    </button>
                    
                    <span style={{ fontSize: '0.9rem', color: '#495057', padding: '0 0.5rem' }}>
                      Página {currentPage} de {totalPages}
                    </span>
                    
                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      style={{
                        padding: '0.25rem 0.75rem',
                        border: '1px solid #ced4da',
                        borderRadius: '4px',
                        background: currentPage === totalPages ? '#e9ecef' : 'white',
                        cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                        fontSize: '0.9rem'
                      }}
                    >
                      Próxima ›
                    </button>
                  </div>
                )}
              </div>

              {/* Ações em massa */}
              <div className="mass-actions">
                <div className="mass-actions-info">
                  {temNotasEditadas ? (
                    <span className="edited-count">
                      {Object.keys(notasEditadas).length} nota(s) editada(s)
                    </span>
                  ) : (
                    <span className="hint-text">
                      💡 Digite as notas nos campos e clique em "Salvar Notas"
                    </span>
                  )}
                </div>
                <div className="mass-actions-buttons">
                  <button
                    onClick={limparNotas}
                    disabled={!temNotasEditadas || saving}
                    className="btn btn-secondary"
                  >
                    <FontAwesomeIcon icon={faUndo} />
                    <span className="btn-text">Limpar</span>
                  </button>
                  <button
                    onClick={salvarNotas}
                    disabled={!temNotasEditadas || saving}
                    className="btn btn-success"
                  >
                    <FontAwesomeIcon icon={saving ? faSpinner : faSave} spin={saving} />
                    <span className="btn-text">
                      {saving ? 'Salvando...' : 'Salvar Notas'}
                    </span>
                  </button>
                </div>
              </div>

              {/* Tabela Desktop */}
              <div className="table-responsive desktop-only">
                <table className="notas-table">
                  <thead>
                    <tr>
                      <th>Aluno</th>
                      <th>RG</th>
                      <th>Curso</th>
                      <th className="nota-column">Nota Atual</th>
                      <th className="nota-column">Nova Nota</th>
                      <th>Status</th>
                      {podeRemoverNotas && <th className="actions-column">Ações</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {alunosPaginados.map(aluno => {
                      const notaAtual = getNotaAtual(aluno);
                      const status = getNotaStatus(notaAtual, aluno.idPessoa);
                      const foiEditado = notasEditadas[aluno.idPessoa] !== undefined;
                      const temNota = aluno.id && aluno.media !== null && aluno.media !== undefined;

                      return (
                        <tr key={aluno.idPessoa} className={foiEditado ? 'edited-row' : ''}>
                          <td>
                            <div className="aluno-nome">{aluno.nomeAluno}</div>
                          </td>
                          <td>{aluno.rgAluno}</td>
                          <td>{aluno.nomeCurso}</td>
                          <td className="nota-column">
                            <span className={`nota-badge ${getNotaStatus(aluno.media)}`}>
                              {aluno.media !== null && aluno.media !== undefined 
                                ? parseFloat(aluno.media).toFixed(2) 
                                : '-'}
                            </span>
                          </td>
                          <td className="nota-column">
                            <input
                              type="number"
                              min="0"
                              max="10"
                              step="0.1"
                              value={notaAtual}
                              onChange={(e) => handleNotaChange(aluno.idPessoa, e.target.value)}
                              className={`nota-input ${status}`}
                              placeholder="Lançar nota"
                              title="Digite a nota de 0 a 10"
                            />
                          </td>
                          <td>
                            {notaAtual && (
                              <span className={`status-badge ${status}`}>
                                {status === 'aprovado' && 'Aprovado'}
                                {status === 'recuperacao' && 'Recuperação'}
                                {status === 'reprovado' && 'Reprovado'}
                                {status === 'invalida' && '⚠ Nota Inválida (0-10)'}
                              </span>
                            )}
                          </td>
                          {podeRemoverNotas && (
                            <td className="actions-column">
                              {temNota ? (
                                <button
                                  onClick={() => handleRemoverNota(aluno.idPessoa, aluno.id)}
                                  className="btn-remove-nota"
                                  title="Remover nota"
                                  disabled={loading}
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                </button>
                              ) : (
                                <span style={{ color: '#ccc', fontSize: '0.75rem' }}>-</span>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Cards Mobile */}
              <div className="mobile-cards mobile-only">
                {alunosPaginados.map(aluno => {
                  const notaAtual = getNotaAtual(aluno);
                  const status = getNotaStatus(notaAtual, aluno.idPessoa);
                  const foiEditado = notasEditadas[aluno.idPessoa] !== undefined;
                  const temNota = aluno.id && aluno.media !== null && aluno.media !== undefined;

                  return (
                    <div key={aluno.idPessoa} className={`aluno-card ${foiEditado ? 'edited' : ''}`}>
                      <div className="card-header">
                        <div className="aluno-info">
                          <h3>{aluno.nomeAluno}</h3>
                          <p className="aluno-rg">RG: {aluno.rgAluno}</p>
                          <p className="aluno-curso">{aluno.nomeCurso}</p>
                        </div>
                        {aluno.media !== null && aluno.media !== undefined && (
                          <div className={`nota-atual ${getNotaStatus(aluno.media)}`}>
                            {parseFloat(aluno.media).toFixed(2)}
                          </div>
                        )}
                      </div>
                      <div className="card-body">
                        <label>Lançar Nota:</label>
                        <input
                          type="number"
                          min="0"
                          max="10"
                          step="0.1"
                          value={notaAtual}
                          onChange={(e) => handleNotaChange(aluno.idPessoa, e.target.value)}
                          className={`nota-input-mobile ${status}`}
                          placeholder="Digite a nota (0-10)"
                        />
                        {notaAtual && (
                          <div className={`status-mobile ${status}`}>
                            {status === 'aprovado' && '✓ Aprovado'}
                            {status === 'recuperacao' && '⚠ Recuperação'}
                            {status === 'reprovado' && '✗ Reprovado'}
                            {status === 'invalida' && '⚠ Nota inválida! Informe um valor entre 0 e 10'}
                          </div>
                        )}
                        
                        {podeRemoverNotas && temNota && (
                          <button
                            onClick={() => handleRemoverNota(aluno.idPessoa, aluno.id)}
                            className="btn-remove-nota-mobile"
                            title="Remover nota"
                            disabled={loading}
                            style={{
                              marginTop: '0.5rem',
                              padding: '0.5rem',
                              width: '100%',
                              background: '#dc3545',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '0.85rem'
                            }}
                          >
                            <FontAwesomeIcon icon={faTrash} style={{ marginRight: '0.5rem' }} />
                            Remover Nota
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}

      {/* Botão fixo mobile para salvar */}
      {temNotasEditadas && (
        <div className="fixed-save-button mobile-only">
          <button
            onClick={salvarNotas}
            disabled={saving}
            className="btn-save-mobile"
          >
            <FontAwesomeIcon icon={saving ? faSpinner : faSave} spin={saving} />
            <span>Salvar {Object.keys(notasEditadas).length} Nota(s)</span>
          </button>
        </div>
      )}
    </div>
  );
}

export default LancamentoNotas;
